import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import portalApi, { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import { FluxWatchMark } from './PortalWidgets';
import {
  TreeNode, FlatRow, QStatus, SidebarFilter, buildSections, flattenNodes, hiddenChildrenOf, indexTree, isNegativeAnswer,
  codeOf, qPalette, QuestionnaireSidebar, QuestionFrame, AnswerControl, ReasonField, SectionHeader, SectionPager, Chip,
  TopBar, ProgressBlock, FilteredList, EmptySection,
} from '../Questionnaires/questionnaireShell';

// The vendor portal is light-only; same palette the shared questionnaire layout uses everywhere.
const P = qPalette(false);
const TOP = 64;
type FilterKey = 'all' | 'unanswered' | 'needs-note' | 'negative' | 'findings' | 'followups';

/* The vendor recipient's actual answering page, gated behind the portal login in
   PortalAuthContext.tsx (see PortalGate/PortalRegister/PortalLogin/PortalVerify in this same
   folder) -- reached at /questionnaire/portal/answer/:responseId, wrapped in
   PortalProtectedRoute in App.tsx. An earlier version of this page was reachable by a bare token
   in the URL with no account at all; that was replaced with the full account/login/verify flow
   per explicit user request, after comparing against UpGuard's own recipient experience.

   The reading pane (sectioned Contents sidebar, live dependency-reveal of hidden follow-up
   questions, sticky progress bar) deliberately reuses the same structure and helpers as
   ../Questionnaires/QuestionnairePreview.tsx -- the Questionnaire Library's own "preview a
   questionnaire" page -- so the two look and behave like one product, not two. The differences
   here are what makes this the *real* answering page rather than a preview: every answer is
   actually saved to the backend as it's given (not held only in local state), there's a welcome
   panel quoting the sender's own message, and a real Submit flow. */

interface Question {
  id: string;
  question_key: string;
  question_text: string;
  question_type: string | null;
  visibility: 'visible' | 'hidden';
  answer_selections: string | null;
  section: string | null;
  depends_on: string | null;
  answer_value: string | null;
  note: string | null;
  ai_suggested?: boolean;
  answered_by_email?: string | null;
}


interface EvidenceDoc {
  id: string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  document_type: string | null;
  created_at: string;
}

interface Comment {
  id: string;
  author_type: 'tenant' | 'vendor';
  author_name: string | null;
  body: string;
  created_at: string;
}

const DOCUMENT_TYPES = ['SOC 2 Report', 'ISO 27001 Certificate', 'Penetration Test Report', 'Security Policy',
  'Privacy Policy', 'Audit Report', 'Insurance Certificate', 'Other'];
const fmtSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface QuestionnaireInfo {
  vendor_id: string;
  recipient_name: string | null;
  message: string | null;
  due_date: string | null;
  status: string;
  is_primary_recipient: boolean;
  eua_accepted: boolean;
  questionnaire: { id: string; name: string; description: string | null; section_titles?: Record<string, string> | null };
  questions: Question[];
}

export default function AnswerQuestionnaire() {
  const { responseId } = useParams<{ responseId: string }>();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState<QuestionnaireInfo | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [aiSuggested, setAiSuggested] = useState<Record<string, boolean>>({});
  const [answeredBy, setAnsweredBy] = useState<Record<string, string>>({});
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [itemByKey, setItemByKey] = useState<Record<string, string>>({}); // question_key -> item id
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sectionKey, setSectionKey] = useState<string | null>(null);
  const pendingScroll = useRef<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // End User Agreement gate -- accepted per questionnaire response, per account (see
  // add_questionnaire_response_eua_acceptances_table.py): a brand-new send asks again even for
  // someone who already accepted it for a different questionnaire, and each collaborator is
  // gated independently from the primary recipient. Resolved from the same /response/open call
  // that loads the questionnaire, so it's always the server's current, authoritative value for
  // *this* response, not a possibly-stale copy from login or from another questionnaire.
  const [euaAccepted, setEuaAccepted] = useState(false);
  const [euaChecked, setEuaChecked] = useState(false);
  const [euaSubmitting, setEuaSubmitting] = useState(false);
  const [euaError, setEuaError] = useState('');

  const acceptEua = async () => {
    if (!responseId) return;
    setEuaSubmitting(true);
    setEuaError('');
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/eua-accept', { response_id: responseId });
      setEuaAccepted(true);
    } catch (err) {
      setEuaError(apiError(err, 'Could not save your agreement. Please try again.'));
    } finally {
      setEuaSubmitting(false);
    }
  };

  const [evidenceDocs, setEvidenceDocs] = useState<EvidenceDoc[]>([]);
  const [evidenceLinks, setEvidenceLinks] = useState<Record<string, string[]>>({});
  const [evidenceError, setEvidenceError] = useState<Record<string, string>>({});
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const [comments, setComments] = useState<Record<string, Comment[]>>({});

  const loadEvidence = () => {
    if (!responseId) return;
    portalApi.get('/v1/questionnaire-responses/portal/evidence', { params: { response_id: responseId } })
      .then(({ data }) => { setEvidenceDocs(data.documents || []); setEvidenceLinks(data.links || {}); })
      .catch(() => { /* the evidence panel just stays empty; answering itself still works */ });
  };

  // One-way: only the tenant reviewer writes these (a finding/recommendation on a "No" or "Not
  // Applicable" answer); the vendor can only read them here, never reply.
  const loadComments = () => {
    if (!responseId) return;
    portalApi.get('/v1/questionnaire-responses/portal/comments', { params: { response_id: responseId } })
      .then(({ data }) => setComments(data.comments || {}))
      .catch(() => { /* findings just stay empty; answering itself still works */ });
  };

  // Reusable so a successful autofill run can refresh answers/notes/ai_suggested in place without
  // a full page reload.
  const loadQuestionnaire = () => {
    if (!responseId) return Promise.resolve();
    return portalApi.get('/v1/questionnaire-responses/portal/response/open', { params: { response_id: responseId } })
      .then(({ data }) => {
        setData(data);
        const initialAnswers: Record<string, string> = {};
        const initialNotes: Record<string, string> = {};
        const initialAiSuggested: Record<string, boolean> = {};
        const initialAnsweredBy: Record<string, string> = {};
        const keyToId: Record<string, string> = {};
        (data.questions || []).forEach((q: Question) => {
          if (q.answer_value) initialAnswers[q.question_key] = q.answer_value;
          if (q.note) initialNotes[q.question_key] = q.note;
          if (q.ai_suggested) initialAiSuggested[q.question_key] = true;
          if (q.answered_by_email) initialAnsweredBy[q.question_key] = q.answered_by_email;
          keyToId[q.question_key] = q.id;
        });
        setAnswers(initialAnswers);
        setNotes(initialNotes);
        setAiSuggested(initialAiSuggested);
        setAnsweredBy(initialAnsweredBy);
        setItemByKey(keyToId);
        setEuaAccepted(!!data.eua_accepted);
        if (data.status === 'answered' || data.status === 'reviewed') setSubmitted(true);
      })
      .catch(err => setLoadError(apiError(err, 'This questionnaire could not be opened.')));
  };

  useEffect(() => {
    loadQuestionnaire().finally(() => setLoading(false));
    loadEvidence();
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseId]);

  const uploadEvidence = async (itemId: string, file: File, documentType: string) => {
    if (!responseId) return;
    setUploadingKey(itemId);
    setEvidenceError(prev => ({ ...prev, [itemId]: '' }));
    try {
      const form = new FormData();
      form.append('response_id', responseId);
      form.append('item_id', itemId);
      form.append('document_type', documentType);
      form.append('file', file);
      await portalApi.post('/v1/questionnaire-responses/portal/evidence', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadEvidence();
    } catch (err) {
      setEvidenceError(prev => ({ ...prev, [itemId]: apiError(err, 'Could not upload that file. Try again.') }));
    } finally {
      setUploadingKey(null);
    }
  };

  const attachExisting = async (itemId: string, documentId: string) => {
    if (!responseId) return;
    setEvidenceLinks(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), documentId] }));
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/response/answer-evidence', { response_id: responseId, item_id: itemId, document_id: documentId });
    } catch {
      loadEvidence(); // out of sync with the server -- resync rather than leave a stuck optimistic state
    }
  };

  const detachEvidence = async (itemId: string, documentId: string) => {
    if (!responseId) return;
    setEvidenceLinks(prev => ({ ...prev, [itemId]: (prev[itemId] || []).filter(id => id !== documentId) }));
    try {
      await portalApi.delete('/v1/questionnaire-responses/portal/response/answer-evidence', { params: { response_id: responseId, item_id: itemId, document_id: documentId } });
    } catch {
      loadEvidence();
    }
  };

  const items = useMemo(() => data?.questions || [], [data]);
  const questionByKey = useMemo(() => Object.fromEntries(items.map(q => [q.question_key, q])), [items]);
  const sections = useMemo(() => buildSections(items, answers), [items, answers]);
  const hidden = useMemo(() => hiddenChildrenOf(items), [items]);
  const allFlat = useMemo(() => sections.flatMap(s => flattenNodes(s.nodes)), [sections]);
  const { sectionOfKey, parentOfKey } = useMemo(() => indexTree(sections), [sections]);

  const isAnswered = (key: string) => !!(answers[key] || '').trim();
  const needsNote = (key: string) => isNegativeAnswer(answers[key]) && !(notes[key] || '').trim();
  const hasFinding = (item: Question) => (comments[item.id] || []).length > 0;
  const statusOf = (item: Question): QStatus =>
    hasFinding(item) ? 'finding' : needsNote(item.question_key) ? 'needs-note' : isAnswered(item.question_key) ? 'answered' : 'unanswered';
  const answeredCount = allFlat.filter(r => isAnswered(r.item.question_key)).length;

  const matchesFilter = (r: FlatRow<Question>, f: FilterKey): boolean => {
    const k = r.item.question_key;
    if (f === 'unanswered') return !isAnswered(k);
    if (f === 'needs-note') return needsNote(k);
    if (f === 'negative') return isNegativeAnswer(answers[k]);
    if (f === 'findings') return hasFinding(r.item);
    if (f === 'followups') return r.depth > 0;
    return true;
  };
  const countFilter = (f: FilterKey) => allFlat.filter(r => matchesFilter(r, f)).length;
  const filters: SidebarFilter[] = [
    { key: 'all', label: 'All', count: allFlat.length, tone: 'blue' },
    { key: 'unanswered', label: 'Unanswered', count: countFilter('unanswered'), tone: 'slate' },
    { key: 'needs-note', label: 'No reason yet', count: countFilter('needs-note'), tone: 'amber' },
    { key: 'negative', label: 'No / N/A', count: countFilter('negative'), tone: 'slate' },
    { key: 'findings', label: 'Findings', count: countFilter('findings'), tone: 'red' },
    { key: 'followups', label: 'Follow-ups', count: countFilter('followups'), tone: 'purple' },
  ];

  // A search or filter can't keep the follow-up tree (a matched follow-up's parent may not match),
  // so it shows a flat list across every section, each follow-up still labelled with its parent.
  const filtering = search.trim() !== '' || filter !== 'all';
  const filtered = useMemo(() => {
    if (!filtering) return null;
    const q = search.trim().toLowerCase();
    return allFlat.filter(r => (!q || r.item.question_text.toLowerCase().includes(q)) && matchesFilter(r, filter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFlat, search, filter, answers, notes, comments, filtering]);

  const current = sections.find(s => s.key === sectionKey) || sections[0];
  const currentIdx = current ? sections.indexOf(current) : 0;
  const sectionTitle = (key: string) => data?.questionnaire.section_titles?.[key] || `Section ${key}`;

  // Scroll only after the target section has rendered -- a jump into another section switches the
  // section first, then scrolls on the next paint.
  useEffect(() => {
    const key = pendingScroll.current;
    if (!key) return;
    pendingScroll.current = null;
    requestAnimationFrame(() => itemRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }, [sectionKey, activeKey, filtering]);

  const setAnswer = (questionKey: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionKey]: value }));
    // Any manual edit confirms the answer -- the backend's own save path already clears
    // ai_suggested for every write that isn't the autofill agent itself; mirror that locally so
    // the "review" badge disappears the instant the vendor touches the question.
    setAiSuggested(prev => (prev[questionKey] ? { ...prev, [questionKey]: false } : prev));
    const itemId = itemByKey[questionKey];
    if (!responseId || !itemId) return;
    portalApi.post('/v1/questionnaire-responses/portal/response/answer', {
      response_id: responseId, item_id: itemId, answer_value: value, note: notes[questionKey] || null,
    }).catch(() => { /* a failed autosave isn't fatal -- it stays in local state and is sent
                        again on the next edit or at submit time */ });
  };

  const setNote = (questionKey: string, value: string) => {
    setNotes(prev => ({ ...prev, [questionKey]: value }));
  };

  // Saved on blur rather than on every keystroke (unlike the answer itself) -- a reason note is
  // free text people pause to think about, not a single click/selection worth autosaving instantly.
  const saveNote = (questionKey: string) => {
    const itemId = itemByKey[questionKey];
    if (!responseId || !itemId) return;
    portalApi.post('/v1/questionnaire-responses/portal/response/answer', {
      response_id: responseId, item_id: itemId, answer_value: answers[questionKey] || null, note: notes[questionKey] || null,
    }).catch(() => { /* stays in local state, retried on the next blur or at submit time */ });
  };

  const jumpTo = (key: string) => {
    setActiveKey(key);
    if (!filtering && sectionOfKey[key]) setSectionKey(sectionOfKey[key]);
    pendingScroll.current = key;
  };

  const selectSection = (key: string) => {
    setSectionKey(key);
    setSearch('');
    setFilter('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!responseId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/response/submit', { response_id: responseId });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(apiError(err, 'Could not submit yet -- every question below needs an answer first.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 12.5, color: T.muted }}>Loading questionnaire…</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 10, padding: 32, textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.red, marginBottom: 6 }}>Can't open this questionnaire</div>
          <div style={{ fontSize: 12.5, color: T.muted }}>{loadError}</div>
        </div>
      </div>
    );
  }
  if (data && !euaAccepted) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 14, padding: 48, width: '100%', maxWidth: 760, boxShadow: '0 8px 32px rgba(15,23,42,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}><FluxWatchMark /></div>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.ink, textAlign: 'center', marginBottom: 8 }}>Before you begin</div>
          <div style={{ fontSize: 15, color: T.muted, textAlign: 'center', marginBottom: 26, lineHeight: 1.5 }}>
            Please read and accept the End User Agreement to answer "{data.questionnaire.name}" for {data.recipient_name || 'your organization'}.
          </div>
          <div style={{
            background: T.surface2, border: `1px solid ${T.bdr}`, borderRadius: 10, padding: '24px 28px',
            maxHeight: 400, overflowY: 'auto', fontSize: 15, lineHeight: 1.75, color: T.ink2, marginBottom: 22,
          }}>
            <p style={{ margin: '0 0 14px' }}>
              By answering this questionnaire through the FluxWatch vendor portal, you confirm that:
            </p>
            <ul style={{ margin: '0 0 14px', paddingLeft: 22 }}>
              <li style={{ marginBottom: 8 }}>the information you provide is accurate and complete to the best of your knowledge, and you will
                correct it if it changes before this questionnaire is reviewed;</li>
              <li style={{ marginBottom: 8 }}>you are authorized to answer on behalf of the vendor named above, and to share the supporting
                documents you attach;</li>
              <li style={{ marginBottom: 8 }}>your answers and any uploaded documents will be visible to the requesting organization
                ({data.recipient_name ? 'the team that invited you' : 'the sender'}) and may be used by them to
                assess this vendor relationship;</li>
              <li>you will keep your portal login confidential and not share it with anyone outside your
                organization.</li>
            </ul>
            <p style={{ margin: 0 }}>
              Full terms:{' '}
              <a href="/vendor-portal-terms" target="_blank" rel="noopener noreferrer" style={{ color: T.primary }}>
                End User Agreement
              </a>.
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 11, cursor: 'pointer', marginBottom: 24 }}>
            <input type="checkbox" checked={euaChecked} onChange={e => setEuaChecked(e.target.checked)}
              style={{ marginTop: 3, width: 19, height: 19, accentColor: T.primary, cursor: 'pointer' }} />
            <span style={{ fontSize: 15.5, color: T.ink2 }}>I have read and agree to the End User Agreement.</span>
          </label>
          {euaError && <div style={{ fontSize: 13.5, color: T.red, marginBottom: 16 }}>{euaError}</div>}
          <button onClick={acceptEua} disabled={!euaChecked || euaSubmitting} style={{
            width: '100%', padding: '15px 0', borderRadius: 10, border: 'none', fontFamily: sans,
            fontSize: 16, fontWeight: 700, color: '#fff', cursor: euaChecked && !euaSubmitting ? 'pointer' : 'not-allowed',
            background: euaChecked ? T.primary : T.bdrStrong, opacity: euaSubmitting ? 0.7 : 1,
          }}>
            {euaSubmitting ? 'Saving…' : 'Agree and continue'}
          </button>
        </div>
      </div>
    );
  }
  if (submitted || !data) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 10, padding: 36, textAlign: 'center', maxWidth: 460 }}>
          <FluxWatchMark />
          <div style={{ fontSize: 30, margin: '18px 0 10px' }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Thanks -- your answers were submitted</div>
          <div style={{ fontSize: 12.5, color: T.muted }}>
            The team that sent this has been notified and will follow up if anything needs a closer look. You can close this page now.
          </div>
        </div>
      </div>
    );
  }

  const evidenceProps = {
    evidenceDocs, evidenceLinks, evidenceError, uploadingKey, onUpload: uploadEvidence,
    onAttach: attachExisting, onDetach: detachEvidence, comments, notes, onNoteChange: setNote, onNoteBlur: saveNote,
    aiSuggested,
    // Shown only to the primary recipient (see PortalResponseDetail.tsx's Collaborators tab for
    // the aggregate view) -- which questions a collaborator, not the recipient, last answered.
    answeredBy: data.is_primary_recipient ? answeredBy : {},
  };

  const parentCodeOf = (key: string) => {
    const parentKey = parentOfKey[key];
    return parentKey && questionByKey[parentKey] ? codeOf(questionByKey[parentKey]) : null;
  };

  const card = (item: Question, depth: number, parentCode: string | null) => (
    <AnswerCard
      key={item.id}
      item={item}
      depth={depth}
      parentCode={parentCode}
      value={answers[item.question_key] || ''}
      onChange={v => setAnswer(item.question_key, v)}
      active={activeKey === item.question_key}
      needsNote={needsNote(item.question_key)}
      setRef={el => { itemRefs.current[item.question_key] = el; }}
      onFocus={() => setActiveKey(item.question_key)}
      {...evidenceProps}
    />
  );

  const renderNode = (node: TreeNode<Question>, depth: number, parentCode: string | null): React.ReactNode => (
    <React.Fragment key={node.item.id}>
      {card(node.item, depth, parentCode)}
      {node.children.map(child => renderNode(child, depth + 1, codeOf(node.item)))}
    </React.Fragment>
  );

  const currentFlat = current ? flattenNodes(current.nodes) : [];
  const currentDone = currentFlat.filter(r => isAnswered(r.item.question_key)).length;
  const dueLabel = data.due_date ? new Date(data.due_date).toLocaleDateString() : null;
  const daysLeft = data.due_date ? Math.ceil((new Date(data.due_date).getTime() - Date.now()) / 86400000) : null;
  const dueSubtitle = dueLabel
    ? `Due ${dueLabel}${daysLeft !== null ? (daysLeft >= 0 ? ` · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : ` · ${-daysLeft} day${daysLeft === -1 ? '' : 's'} overdue`) : ''}`
    : undefined;

  const submitControl = data.is_primary_recipient ? (
    <button
      type="button"
      disabled={submitting}
      onClick={handleSubmit}
      style={{
        flexShrink: 0, padding: '9px 18px', borderRadius: 8, border: 'none', background: P.blue, color: '#fff',
        fontSize: 13, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', fontFamily: sans, opacity: submitting ? 0.7 : 1,
      }}
    >
      {submitting ? 'Submitting…' : 'Submit answers'}
    </button>
  ) : (
    <div style={{ flexShrink: 0, fontSize: 11.5, color: P.muted, textAlign: 'right', maxWidth: 200, lineHeight: 1.4 }}>
      Saved as you go. Only {data.recipient_name || 'the recipient'} can submit.
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: P.bg, fontFamily: sans, fontSize: 15 }}>
      <TopBar p={P} height={TOP}>
        <FluxWatchMark small />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.questionnaire.name}
          </div>
          <div style={{ fontSize: 12, color: P.muted }}>{dueLabel ? `Due ${dueLabel} · ` : ''}Your progress is saved as you go</div>
        </div>
        <button
          type="button"
          onClick={() => setAutofillOpen(true)}
          style={{
            flexShrink: 0, padding: '8px 13px', borderRadius: 8, border: `1px solid ${P.lineStrong}`, background: P.surface,
            color: P.teal, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: sans,
          }}
        >
          ✦ Autofill from evidence
        </button>
        {data.is_primary_recipient && responseId && (
          <Link
            to={`/questionnaire/portal/response/${responseId}`}
            style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: P.blue, textDecoration: 'none' }}
          >
            Collaborators
          </Link>
        )}
        <ProgressBlock p={P} done={answeredCount} total={allFlat.length} />
        {submitControl}
      </TopBar>
      {submitError && (
        <div role="alert" style={{ background: P.redBg, color: P.red, fontSize: 12.5, padding: '9px 24px', borderBottom: `1px solid ${P.line}` }}>
          {submitError}
        </div>
      )}

      <div style={{ display: 'flex' }}>
        <QuestionnaireSidebar
          p={P}
          font={sans}
          top={TOP}
          sections={sections}
          sectionTitle={sectionTitle}
          hidden={hidden}
          statusOf={statusOf}
          isAnswered={item => isAnswered(item.question_key)}
          activeSection={current?.key || null}
          onSelectSection={selectSection}
          activeKey={activeKey}
          onJump={jumpTo}
          search={search}
          onSearch={setSearch}
          filters={filters}
          filter={filter}
          onFilter={k => setFilter(k as FilterKey)}
          filtered={filtered}
          answeredCount={answeredCount}
          totalCount={allFlat.length}
          subtitle={dueSubtitle}
          showFindingLegend
        />

        <main style={{ flex: 1, minWidth: 0 }}>
          <div style={{ width: '90%', margin: '0 auto', padding: '28px 0 80px' }}>
            {filtered ? (
              <FilteredList
                p={P}
                rows={filtered}
                sectionTitle={sectionTitle}
                onClear={() => { setSearch(''); setFilter('all'); }}
                renderRow={r => card(r.item, r.depth ? 1 : 0, r.depth ? parentCodeOf(r.item.question_key) : null)}
              />
            ) : current ? (
              <>
                {currentIdx === 0 && (data.questionnaire.description || data.message) && (
                  <div style={{ background: P.surface, border: `1px solid ${P.line}`, borderRadius: 12, padding: '16px 20px', marginBottom: 22 }}>
                    {data.questionnaire.description && (
                      <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.6 }}>{data.questionnaire.description}</div>
                    )}
                    {data.message && (
                      <div style={{ marginTop: data.questionnaire.description ? 12 : 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: P.faint, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>
                          Message from the sender
                        </div>
                        <div style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.message}</div>
                      </div>
                    )}
                  </div>
                )}
                <SectionHeader p={P} title={sectionTitle(current.key)} index={currentIdx} count={sections.length} done={currentDone} total={currentFlat.length} />
                {current.nodes.length ? current.nodes.map(node => renderNode(node, 0, null)) : <EmptySection p={P} />}
                <SectionPager
                  p={P}
                  font={sans}
                  prevTitle={currentIdx > 0 ? sectionTitle(sections[currentIdx - 1].key) : null}
                  nextTitle={currentIdx < sections.length - 1 ? sectionTitle(sections[currentIdx + 1].key) : null}
                  onPrev={() => selectSection(sections[currentIdx - 1].key)}
                  onNext={() => selectSection(sections[currentIdx + 1].key)}
                  end={submitControl}
                />
              </>
            ) : (
              <div style={{ fontSize: 13, color: P.muted, textAlign: 'center', padding: 48 }}>This questionnaire has no questions.</div>
            )}
          </div>
        </main>
      </div>

      {autofillOpen && (
        <AutofillModal
          evidenceDocs={evidenceDocs}
          onClose={() => setAutofillOpen(false)}
          onRun={async (documentIds, emptyOnly) => {
            if (!responseId) return { ok: false, error: 'No response' };
            try {
              const { data } = await portalApi.post('/v1/questionnaire-responses/portal/response/autofill', {
                response_id: responseId, document_ids: documentIds, empty_only: emptyOnly,
              });
              await loadQuestionnaire();
              return { ok: true, answered: data.answered, skipped: data.skipped };
            } catch (err) {
              return { ok: false, error: apiError(err, 'Could not run autofill. Try again.') };
            }
          }}
        />
      )}
    </div>
  );
}

interface EvidenceProps {
  evidenceDocs: EvidenceDoc[];
  evidenceLinks: Record<string, string[]>;
  evidenceError: Record<string, string>;
  uploadingKey: string | null;
  onUpload: (itemId: string, file: File, documentType: string) => void;
  onAttach: (itemId: string, documentId: string) => void;
  onDetach: (itemId: string, documentId: string) => void;
  comments: Record<string, Comment[]>;
  notes: Record<string, string>;
  onNoteChange: (questionKey: string, value: string) => void;
  onNoteBlur: (questionKey: string) => void;
  aiSuggested: Record<string, boolean>;
  answeredBy: Record<string, string>;
}

function AnswerCard({ item, depth, parentCode, value, onChange, active, needsNote, setRef, onFocus, evidenceDocs, evidenceLinks, evidenceError, uploadingKey, onUpload, onAttach, onDetach, comments, notes, onNoteChange, onNoteBlur, aiSuggested, answeredBy }: {
  item: Question; depth: number; parentCode: string | null; value: string; onChange: (v: string) => void; active: boolean;
  needsNote: boolean; setRef: (el: HTMLDivElement | null) => void; onFocus: () => void;
} & EvidenceProps) {
  const key = item.question_key;
  const badges = (aiSuggested[key] || answeredBy[key]) ? (
    <>
      {aiSuggested[key] && <Chip p={P} tone="teal">✦ Suggested by AI from your evidence · review before submitting</Chip>}
      {answeredBy[key] && <Chip p={P} tone="slate">Answered by {answeredBy[key]}</Chip>}
    </>
  ) : undefined;

  return (
    <QuestionFrame
      p={P}
      font={sans}
      item={item}
      depth={depth}
      parentCode={parentCode}
      active={active}
      needsNote={needsNote}
      setRef={setRef}
      onFocus={onFocus}
      badges={badges}
    >
      <AnswerControl p={P} font={sans} item={item} value={value} onChange={onChange} onFocus={onFocus} />
      {isNegativeAnswer(value) && (
        <ReasonField
          p={P}
          font={sans}
          value={notes[key] || ''}
          onChange={v => onNoteChange(key, v)}
          onBlur={() => onNoteBlur(key)}
          onFocus={onFocus}
        />
      )}
      <EvidencePanel
        itemId={item.id}
        docs={evidenceDocs}
        attachedIds={evidenceLinks[item.id] || []}
        error={evidenceError[item.id]}
        uploading={uploadingKey === item.id}
        onUpload={(file, documentType) => onUpload(item.id, file, documentType)}
        onAttach={documentId => onAttach(item.id, documentId)}
        onDetach={documentId => onDetach(item.id, documentId)}
      />
      {(comments[item.id] || []).length > 0 && (
        <FindingsPanel thread={comments[item.id]} />
      )}
    </QuestionFrame>
  );
}

const PaperclipIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

// Optional on every question: attach an already-uploaded library document, or upload a new one
// (which also joins the shared library for this vendor relationship, see PortalResponseDetail.tsx's
// Documents tab). A quiet row until something is attached -- no tinted or dashed box.
function EvidencePanel({ itemId, docs, attachedIds, error, uploading, onUpload, onAttach, onDetach }: {
  itemId: string; docs: EvidenceDoc[]; attachedIds: string[]; error?: string; uploading: boolean;
  onUpload: (file: File, documentType: string) => void; onAttach: (documentId: string) => void; onDetach: (documentId: string) => void;
}) {
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]);
  const [picking, setPicking] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attached = docs.filter(d => attachedIds.includes(d.id));
  const available = docs.filter(d => !attachedIds.includes(d.id));

  const linkBtn: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, color: P.blue, fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        {attached.map(doc => (
          <span key={doc.id} title={`${doc.document_type || 'Document'}${doc.size_bytes ? ` · ${fmtSize(doc.size_bytes)}` : ''}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 9px', borderRadius: 99, fontSize: 12,
            border: `1px solid ${P.line}`, background: P.surface2, color: P.ink, maxWidth: 320,
          }}>
            <PaperclipIcon />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</span>
            <button type="button" onClick={() => onDetach(doc.id)} aria-label={`Remove ${doc.file_name}`} style={{
              background: 'none', border: 'none', color: P.faint, cursor: 'pointer', fontSize: 14, padding: '0 3px', lineHeight: 1,
            }}>×</button>
          </span>
        ))}
        {!formOpen && (
          <button type="button" onClick={() => setFormOpen(true)} style={linkBtn}>
            + {attached.length ? 'Add another document' : 'Attach evidence'}
          </button>
        )}
      </div>

      {formOpen && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${P.line}` }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={documentType} onChange={e => setDocumentType(e.target.value)} style={{
              padding: '7px 10px', fontSize: 12.5, fontFamily: sans, color: P.ink, background: P.surface, border: `1px solid ${P.line}`, borderRadius: 7, outline: 'none',
            }}>
              {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              key={itemId}
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
              style={{ display: 'none' }}
              onChange={e => { const file = e.target.files?.[0]; if (file) onUpload(file, documentType); e.target.value = ''; setFormOpen(false); }}
            />
            <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} style={{
              padding: '7px 13px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, fontFamily: sans,
              border: `1px solid ${P.blue}`, background: P.blue, color: '#fff', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1,
            }}>{uploading ? 'Uploading…' : 'Upload file'}</button>
            {available.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button type="button" onClick={() => setPicking(v => !v)} style={{
                  padding: '7px 13px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, fontFamily: sans,
                  border: `1px solid ${P.lineStrong}`, background: P.surface, color: P.ink, cursor: 'pointer',
                }}>Use an existing document ▾</button>
                {picking && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20, minWidth: 240, maxHeight: 220, overflowY: 'auto', background: P.surface, border: `1px solid ${P.line}`, borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.12)' }}>
                    {available.map(doc => (
                      <button key={doc.id} type="button" onClick={() => { onAttach(doc.id); setPicking(false); setFormOpen(false); }} style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: sans,
                        borderBottom: `1px solid ${P.line}`,
                      }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
                        <div style={{ fontSize: 11, color: P.muted }}>{doc.document_type}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button type="button" onClick={() => { setFormOpen(false); setPicking(false); }} style={{ ...linkBtn, color: P.muted, fontWeight: 500 }}>Cancel</button>
          </div>
          <div style={{ fontSize: 11, color: P.muted, marginTop: 7 }}>PDF, Word, Excel, image or text file, up to 15MB. Uploaded files are reused for other questions and future requests.</div>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: P.red, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

type AutofillOutcome = { ok: true; answered: number; skipped: number } | { ok: false; error: string };

// Pick one or more documents already in the evidence library, run the autofill agent, and show a
// plain summary -- same shape as UpGuard's own "Autofill this questionnaire" flow (confirmed live
// in a real UpGuard trial before building this): never auto-submits, every suggestion stays
// editable and shows a "review" badge on the question itself (see aiSuggested above).
function AutofillModal({ evidenceDocs, onClose, onRun }: {
  evidenceDocs: EvidenceDoc[];
  onClose: () => void;
  onRun: (documentIds: string[], emptyOnly: boolean) => Promise<AutofillOutcome>;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [emptyOnly, setEmptyOnly] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AutofillOutcome | null>(null);

  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const run = async () => {
    if (!selected.length || running) return;
    setRunning(true);
    const outcome = await onRun(selected, emptyOnly);
    setRunning(false);
    setResult(outcome);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: T.surface, borderRadius: 12, padding: '22px 24px', fontFamily: sans }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Autofill from evidence</div>

        {result ? (
          !result.ok ? (
            <>
              <div style={{ fontSize: 13, color: T.red, margin: '14px 0' }}>{result.error}</div>
              <button onClick={onClose} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: T.ink, color: '#fff', fontWeight: 700, fontFamily: sans, cursor: 'pointer' }}>Close</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, margin: '14px 0' }}>
                <strong style={{ color: T.green }}>{result.answered}</strong> question{result.answered === 1 ? '' : 's'} answered from your evidence.{' '}
                {result.skipped > 0 && <>{result.skipped} question{result.skipped === 1 ? '' : 's'} didn't have a clear match and were left for you to fill in.</>}
                {' '}Review the <span style={{ color: P.teal, fontWeight: 600 }}>✦ Suggested by AI</span> badges before submitting.
              </div>
              <button onClick={onClose} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: T.ink, color: '#fff', fontWeight: 700, fontFamily: sans, cursor: 'pointer' }}>Done</button>
            </>
          )
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6, margin: '8px 0 14px' }}>
              Pick one or more documents from your evidence library. AI will suggest answers for the
              questions they clearly cover -- every suggestion stays editable, nothing is submitted
              automatically.
            </div>
            {evidenceDocs.length === 0 ? (
              <div style={{ fontSize: 12.5, color: T.muted, padding: '14px 0' }}>
                No documents yet -- attach a SOC 2 report or similar to any question first, then come back here.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', marginBottom: 14 }}>
                {evidenceDocs.map(doc => (
                  <label key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', borderRadius: 7,
                    border: `1px solid ${selected.includes(doc.id) ? T.blue : T.bdr}`,
                    background: selected.includes(doc.id) ? T.blueBg : 'none', cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={selected.includes(doc.id)} onChange={() => toggle(doc.id)} />
                    <span style={{ fontSize: 12.5, color: T.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</span>
                    <span style={{ fontSize: 11, color: T.muted, flexShrink: 0, marginLeft: 'auto' }}>{doc.document_type}</span>
                  </label>
                ))}
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.ink, marginBottom: 18, cursor: 'pointer' }}>
              <input type="checkbox" checked={emptyOnly} onChange={e => setEmptyOnly(e.target.checked)} />
              Only fill unanswered questions
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={running} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${T.bdr}`, background: 'none', color: T.ink, fontWeight: 600, fontFamily: sans, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={run} disabled={!selected.length || running} style={{
                flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: P.teal, color: '#fff', fontWeight: 700, fontFamily: sans,
                cursor: !selected.length || running ? 'default' : 'pointer', opacity: !selected.length || running ? 0.6 : 1,
              }}>
                {running ? 'Answering…' : 'Run autofill'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CommentIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
  </svg>
);
const timeAgo = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// One-way: the tenant reviewer's finding/recommendation on this question, shown read-only to the
// vendor whenever one exists, regardless of the answer -- the vendor never composes or replies here.
// Red is reserved for this across the page (sidebar dot, "Findings" filter, this label).
function FindingsPanel({ thread }: { thread: Comment[] }) {
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${P.line}` }}>
      <div style={{ marginBottom: 10 }}>
        <Chip p={P} tone="red"><CommentIcon /> {thread.length > 1 ? `Reviewer findings (${thread.length})` : 'Reviewer finding'}</Chip>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {thread.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 9 }}>
            <span style={{
              width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10.5, fontWeight: 700, color: P.red, background: P.redBg,
            }}>{(c.author_name || '?').slice(0, 1).toUpperCase()}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: P.ink }}>{c.author_name || 'Reviewer'}</span>
                <span style={{ fontSize: 10.5, color: P.muted }}>reviewer · {timeAgo(c.created_at)}</span>
              </div>
              <div style={{ fontSize: 13, color: P.ink, lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
